import { useState } from 'react';
import api from '../../services/api';
import { Goal, GoalStep, Task } from './types';

export const useGoalSteps = (loadData: () => Promise<any>) => {
  const [selectedGoalForSteps, setSelectedGoalForSteps] = useState<Goal | null>(null);
  const [expandedStepId, setExpandedStepId] = useState<number | null>(null);
  const [expandedStepOnCard, setExpandedStepOnCard] = useState<number | null>(null);
  
  const [stepForm, setStepForm] = useState({
    name: '',
    description: ''
  });

  const handleAddStep = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGoalForSteps) return;

    try {
      await api.post('/api/goal-steps', {
        goalId: selectedGoalForSteps.id,
        name: stepForm.name,
        description: stepForm.description
      });
      setStepForm({ name: '', description: '' });
      const updatedGoals = await loadData();
      
      const updatedGoal = updatedGoals.find((g: Goal) => g.id === selectedGoalForSteps.id);
      if (updatedGoal) setSelectedGoalForSteps(updatedGoal);
    } catch (error) {
      console.error('Failed to add step:', error);
      alert('Ошибка при добавлении шага');
    }
  };

  const handleToggleStep = async (stepId: number, completed: boolean) => {
    try {
      await api.put(`/api/goal-steps/${stepId}`, { completed: !completed });
      const updatedGoals = await loadData();
      
      if (selectedGoalForSteps) {
        const updatedGoal = updatedGoals.find((g: Goal) => g.id === selectedGoalForSteps.id);
        if (updatedGoal) setSelectedGoalForSteps(updatedGoal);
      }
    } catch (error) {
      console.error('Failed to toggle step:', error);
    }
  };

  const handleDeleteStep = async (stepId: number) => {
    if (window.confirm('Удалить этот шаг?')) {
      try {
        await api.delete(`/api/goal-steps/${stepId}`);
        const updatedGoals = await loadData();
        
        if (selectedGoalForSteps) {
          const updatedGoal = updatedGoals.find((g: Goal) => g.id === selectedGoalForSteps.id);
          if (updatedGoal) setSelectedGoalForSteps(updatedGoal);
        }
      } catch (error) {
        console.error('Failed to delete step:', error);
      }
    }
  };

  const handleMoveStepUp = async (goalId: number, stepId: number, stepIndex: number) => {
    try {
      const goal = selectedGoalForSteps;
      if (!goal || !goal.steps) return;
      
      const sortedSteps = [...goal.steps].sort((a, b) => a.order - b.order);
      if (stepIndex === 0) return;
      
      const currentStep = sortedSteps[stepIndex];
      const previousStep = sortedSteps[stepIndex - 1];
      
      const tempOrder = -1000 - currentStep.id;
      const targetOrder = previousStep.order;
      const swapOrder = currentStep.order;
      
      await api.put(`/api/goal-steps/${currentStep.id}`, { order: tempOrder });
      await api.put(`/api/goal-steps/${previousStep.id}`, { order: swapOrder });
      await api.put(`/api/goal-steps/${currentStep.id}`, { order: targetOrder });
      
      const updatedGoals = await loadData();
      const updatedGoal = updatedGoals.find((g: Goal) => g.id === selectedGoalForSteps.id);
      if (updatedGoal) setSelectedGoalForSteps(updatedGoal);
    } catch (error) {
      console.error('Failed to move step up:', error);
      alert('Ошибка при перемещении шага');
    }
  };

  const handleMoveStepDown = async (goalId: number, stepId: number, stepIndex: number) => {
    try {
      const goal = selectedGoalForSteps;
      if (!goal || !goal.steps) return;
      
      const sortedSteps = [...goal.steps].sort((a, b) => a.order - b.order);
      if (stepIndex === sortedSteps.length - 1) return;
      
      const currentStep = sortedSteps[stepIndex];
      const nextStep = sortedSteps[stepIndex + 1];
      
      const tempOrder = -1000 - currentStep.id;
      const targetOrder = nextStep.order;
      const swapOrder = currentStep.order;
      
      await api.put(`/api/goal-steps/${currentStep.id}`, { order: tempOrder });
      await api.put(`/api/goal-steps/${nextStep.id}`, { order: swapOrder });
      await api.put(`/api/goal-steps/${currentStep.id}`, { order: targetOrder });
      
      const updatedGoals = await loadData();
      const updatedGoal = updatedGoals.find((g: Goal) => g.id === selectedGoalForSteps.id);
      if (updatedGoal) setSelectedGoalForSteps(updatedGoal);
    } catch (error) {
      console.error('Failed to move step down:', error);
      alert('Ошибка при перемещении шага');
    }
  };

  const handleAttachTaskToStep = async (taskId: number, stepId: number) => {
    try {
      if (!selectedGoalForSteps) return;
      
      // Привязываем дело к шагу И назначаем цель
      await api.put(`/api/tasks/${taskId}`, { 
        stepId,
        goalId: selectedGoalForSteps.id 
      });
      
      const updatedGoals = await loadData();
      
      // Обновляем модальное окно с новыми данными
      const updatedGoal = updatedGoals.find((g: Goal) => g.id === selectedGoalForSteps.id);
      if (updatedGoal) setSelectedGoalForSteps(updatedGoal);
      
      setExpandedStepOnCard(null);
    } catch (error) {
      console.error('Failed to attach task to step:', error);
    }
  };

  const handleDetachTaskFromStep = async (taskId: number) => {
    try {
      await api.put(`/api/tasks/${taskId}`, { stepId: null });
      const updatedGoals = await loadData();
      
      // Обновляем модальное окно с новыми данными
      if (selectedGoalForSteps) {
        const updatedGoal = updatedGoals.find((g: Goal) => g.id === selectedGoalForSteps.id);
        if (updatedGoal) setSelectedGoalForSteps(updatedGoal);
      }
    } catch (error) {
      console.error('Failed to detach task from step:', error);
    }
  };

  return {
    selectedGoalForSteps,
    setSelectedGoalForSteps,
    expandedStepId,
    setExpandedStepId,
    expandedStepOnCard,
    setExpandedStepOnCard,
    stepForm,
    setStepForm,
    handleAddStep,
    handleToggleStep,
    handleDeleteStep,
    handleMoveStepUp,
    handleMoveStepDown,
    handleAttachTaskToStep,
    handleDetachTaskFromStep
  };
};
